import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { MapPin, Pencil, X, Check } from 'lucide-react';
import { AddressAutocompleteInput } from '../AddressAutocompleteInput';
import { ApartmentMapPreview } from '../ApartmentMapPreview';
import { useUpdateApartmentAddress } from '../../hooks/useApartments';
import { ResolvedPlaceDetails } from '../../utils/extractAddressComponents';
import type { Apartment } from '../../types/apartment';

const schema = z.object({
  street: z.string().min(1, 'forms:required'),
  city: z.string().min(1, 'forms:required'),
  country: z.string().min(1, 'forms:required'),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  placeId: z.string().optional(),
  formattedAddress: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface LocationSectionProps {
  readonly apartment: Apartment;
}

export const LocationSection = ({ apartment }: LocationSectionProps) => {
  const { t } = useTranslation(['common', 'forms', 'errors']);
  const [isEditing, setIsEditing] = useState(false);
  const updateMutation = useUpdateApartmentAddress();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      street: apartment.address.street,
      city: apartment.address.city,
      country: apartment.address.country,
      state: apartment.address.state,
      postalCode: apartment.address.postalCode,
      latitude: apartment.address.latitude,
      longitude: apartment.address.longitude,
      placeId: apartment.address.placeId ?? undefined,
      formattedAddress: apartment.address.formattedAddress ?? undefined,
    },
  });

  const watchedLat = watch('latitude') ?? 0;
  const watchedLng = watch('longitude') ?? 0;

  const handleEdit = () => {
    reset({
      street: apartment.address.street,
      city: apartment.address.city,
      country: apartment.address.country,
      state: apartment.address.state,
      postalCode: apartment.address.postalCode,
      latitude: apartment.address.latitude,
      longitude: apartment.address.longitude,
      placeId: apartment.address.placeId ?? undefined,
      formattedAddress: apartment.address.formattedAddress ?? undefined,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handlePlaceSelected = (place: ResolvedPlaceDetails) => {
    setValue('street', place.street ?? '');
    setValue('city', place.city ?? '');
    setValue('country', place.country ?? '');
    setValue('state', place.state ?? undefined);
    setValue('postalCode', place.postalCode ?? undefined);
    setValue('latitude', place.latitude);
    setValue('longitude', place.longitude);
    setValue('placeId', place.placeId);
    setValue('formattedAddress', place.formattedAddress);
  };

  const handleCleared = () => {
    setValue('latitude', undefined);
    setValue('longitude', undefined);
    setValue('placeId', undefined);
    setValue('formattedAddress', undefined);
  };

  const onSubmit = async (data: FormData) => {
    try {
      await updateMutation.mutateAsync({
        id: apartment.id,
        data: {
          street: data.street,
          city: data.city,
          country: data.country,
          state: data.state,
          postalCode: data.postalCode,
          latitude: data.latitude,
          longitude: data.longitude,
          placeId: data.placeId,
          formattedAddress: data.formattedAddress,
        },
      });
      setIsEditing(false);
    } catch {
      // error shown inline
    }
  };

  const displayAddress = apartment.address.formattedAddress ||
    [apartment.address.street, apartment.address.city, apartment.address.country].filter(Boolean).join(', ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('forms:location')}</h2>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            aria-label={t('common:edit')}
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:addressSearch')}</label>
            <AddressAutocompleteInput
              initialValue={apartment.address.formattedAddress ?? undefined}
              onPlaceSelected={handlePlaceSelected}
              onCleared={handleCleared}
              disabled={updateMutation.isPending}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:street')}</label>
              <input {...register('street')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {errors.street && <p className="text-sm text-red-600 mt-1">{errors.street.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:city')}</label>
              <input {...register('city')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {errors.city && <p className="text-sm text-red-600 mt-1">{errors.city.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:country')}</label>
              <input {...register('country')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {errors.country && <p className="text-sm text-red-600 mt-1">{errors.country.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:state')}</label>
              <input {...register('state')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          {(watchedLat !== 0 || watchedLng !== 0) && (
            <ApartmentMapPreview lat={watchedLat} lng={watchedLng} />
          )}
          {updateMutation.isError && (
            <p className="text-sm text-red-600">{t('errors:somethingWentWrong')}</p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 min-h-[44px]"
            >
              <Check size={16} />
              {updateMutation.isPending ? t('common:saving') : t('common:save')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium min-h-[44px]"
            >
              <X size={16} />
              {t('common:cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <MapPin size={16} className="text-primary-500 mt-0.5 shrink-0" />
            <span>{displayAddress}</span>
          </div>
          {apartment.address.latitude && apartment.address.longitude && (
            <ApartmentMapPreview lat={apartment.address.latitude} lng={apartment.address.longitude} />
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSection;
